import { Box, useTheme } from '@material-ui/core';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { useAgent } from '../../hooks/use-agent';
import { useLocalStorage } from '../../hooks/use-local-storage';
import {
    contentStyles,
    createStyles,
    DEFAULT_SIDEBAR_HEIGHT,
    headerDragStyles,
    SIDE_SPACING,
    TOP_SPACING
} from './agent-portal.styles';
import { AgentResizeHandles } from './agent-resize-handles';
import { AgentSidebar } from './agent-sidebar';
import { useAgentResize } from './use-agent-resize';

export const AgentPortal: React.FC = () => {
    const location = useLocation();
    const { open, dockSide, isDocked, position, width, onClose, onUndock, onUpdatePosition, onSetWidth } = useAgent();
    const theme = useTheme();
    const portalContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [height, setHeight] = useLocalStorage('agent-sidebar-height', DEFAULT_SIDEBAR_HEIGHT);

    const {
        handleDragStart,
        handleFloatingResizeStart,
        handleResizeHeightStart,
        handleResizeWidthStart,
        isDragging,
        isResizingHeight,
        isResizingWidth,
        resizingEdge
    } = useAgentResize({
        dockSide,
        height,
        isDocked,
        onUndock,
        onUpdatePosition,
        portalContainerRef,
        position,
        setHeight,
        setWidth: onSetWidth,
        width
    });

    // Initialize portal container
    React.useEffect(() => {
        if (typeof document === 'undefined') {
            return undefined;
        }

        if (!portalContainerRef.current) {
            const el = document.createElement('div');
            el.setAttribute('data-agent-sidebar-root', 'true');
            document.body.appendChild(el);
            portalContainerRef.current = el;
        }

        return () => {
            if (portalContainerRef.current && portalContainerRef.current.parentElement) {
                portalContainerRef.current.parentElement.removeChild(portalContainerRef.current);
            }
            portalContainerRef.current = null;
        };
    }, []);

    // Handle window resize and initial load to re-snap position to honor margins
    React.useEffect(() => {
        if (!isDocked && typeof window !== 'undefined') {
            const constrainPosition = () => {
                // Re-snap to honor SIDE_SPACING and TOP_SPACING margins on all sides
                const minX = SIDE_SPACING;
                const minY = TOP_SPACING;
                const maxX = window.innerWidth - width - SIDE_SPACING;
                const maxY = window.innerHeight - height - SIDE_SPACING;

                const constrainedX = Math.max(minX, Math.min(position.x, maxX));
                const constrainedY = Math.max(minY, Math.min(position.y, maxY));

                if (constrainedX !== position.x || constrainedY !== position.y) {
                    onUpdatePosition({ x: constrainedX, y: constrainedY });
                }
            };

            // Constrain on initial mount and when window resizes
            constrainPosition();

            window.addEventListener('resize', constrainPosition);
            return () => window.removeEventListener('resize', constrainPosition);
        }
        return undefined;
    }, [isDocked, position, width, height, onUpdatePosition]);

    React.useEffect(() => {
        if (typeof document === 'undefined' || !open) return undefined;

        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [open, onClose]);

    // Attach drag handler to header element (both docked and floating)
    React.useEffect(() => {
        if (!open || !portalContainerRef.current) {
            return undefined;
        }

        const headerElement = portalContainerRef.current.querySelector('.agent__header');
        if (!headerElement) {
            return undefined;
        }

        const handleHeaderMouseDown = (e: MouseEvent) => {
            handleDragStart(e);
        };

        headerElement.addEventListener('mousedown', handleHeaderMouseDown);
        return () => {
            headerElement.removeEventListener('mousedown', handleHeaderMouseDown);
        };
    }, [open, handleDragStart, isDocked]);

    if (location.pathname.startsWith('/orbit')) {
        return null;
    }

    const sidebarContent = (
        <Box
            component="aside"
            css={createStyles(theme, open, isDocked, dockSide, width, height, position)}
            role="complementary"
            aria-hidden={!open}
        >
            <AgentResizeHandles
                isDocked={isDocked}
                dockSide={dockSide}
                isResizingWidth={isResizingWidth}
                isResizingHeight={isResizingHeight}
                resizingEdge={resizingEdge}
                onResizeWidthStart={handleResizeWidthStart}
                onResizeHeightStart={handleResizeHeightStart}
                onFloatingResizeStart={handleFloatingResizeStart}
            />
            <div css={contentStyles}>
                <div css={headerDragStyles(isDragging)}>
                    <AgentSidebar />
                </div>
            </div>
        </Box>
    );

    if (!portalContainerRef.current) {
        if (typeof document === 'undefined') {
            return null;
        }
        const el = document.createElement('div');
        el.setAttribute('data-agent-sidebar-root', 'true');
        document.body.appendChild(el);
        portalContainerRef.current = el;
    }

    return createPortal(sidebarContent, portalContainerRef.current);
};
